import type { ComponentType } from 'react';
import { serializeDescriptor, type ComponentDescriptor, type SerializedComponentDescriptor } from './descriptor';

/**
 * The registry container, kept in a leaf module.
 *
 * `src/registry.ts` imports every component barrel in order to *populate* a registry. Components that
 * themselves need a registry (today: the Studio `ScreenDesigner`, for its palette and property schemas)
 * therefore cannot import `src/registry.ts` at module scope — doing so closes a cycle
 * `b.barrel → ScreenDesigner → registry.ts → studio/index.ts → b.barrel`, and whichever module the
 * bundler happens to enter first reads the other's aggregate array while it is still in its temporal
 * dead zone. That is what used to blank the playground with
 * "Cannot access 'screenDesignerDescriptor' before initialization".
 *
 * This module holds the class and a late-bound accessor, and imports nothing but the descriptor types,
 * so components can depend on it freely. `src/registry.ts` installs the real factory when it is evaluated.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyComponent = ComponentType<any>;

export interface RegistryEntry {
  descriptor: ComponentDescriptor<unknown>;
  component: AnyComponent;
}

/** Runtime component registry consumed by the metadata renderer and Studio. */
export class ComponentRegistry {
  private readonly entries = new Map<string, RegistryEntry>();

  constructor(entries: RegistryEntry[] = []) {
    for (const e of entries) this.register(e.descriptor, e.component);
  }

  register(descriptor: ComponentDescriptor<unknown>, component: AnyComponent): void {
    this.entries.set(descriptor.name, { descriptor, component });
  }

  get(name: string): RegistryEntry | undefined {
    return this.entries.get(name);
  }

  has(name: string): boolean {
    return this.entries.has(name);
  }

  names(): string[] {
    return Array.from(this.entries.keys());
  }

  list(): RegistryEntry[] {
    return Array.from(this.entries.values());
  }

  byCategory(category: ComponentDescriptor['category']): RegistryEntry[] {
    return this.list().filter((e) => e.descriptor.category === category);
  }

  /** JSON representation published as component-registry.json for backend validation. */
  toJSON(): { version: string; components: SerializedComponentDescriptor[] } {
    return {
      version: '1',
      components: this.list().map((e) => serializeDescriptor(e.descriptor)),
    };
  }
}

let factory: (() => ComponentRegistry) | null = null;
let resolved: ComponentRegistry | null = null;

/** Installed by `src/registry.ts` when it is evaluated; the only writer. */
export function setDefaultRegistryFactory(create: () => ComponentRegistry): void {
  factory = create;
  resolved = null;
}

/**
 * The default registry, resolved on first *call* rather than on import.
 *
 * Callers are components, so by the time this runs the application has imported the package barrel (or
 * `src/registry.ts` directly) and the factory is installed. If a consumer somehow deep-imports a
 * registry-aware component and nothing else, this degrades to an empty registry rather than throwing —
 * and every such component also accepts an explicit `registry` prop.
 */
export function resolveDefaultRegistry(): ComponentRegistry {
  if (!resolved) resolved = factory ? factory() : new ComponentRegistry();
  return resolved;
}
